import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const languages = [
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'en', name: 'English',   flag: '🇬🇧' },
  { code: 'fr', name: 'Français',  flag: '🇫🇷' }
];

export default function LanguageSwitcher({ compact = false }) {
  const { i18n, t } = useTranslation();
  const current = (i18n.language || 'pt').slice(0, 2);
  const currentLang = languages.find((l) => l.code === current) || languages[0];

  const changeLanguage = (lng) => {
    if (lng === current) return;
    i18n.changeLanguage(lng); // also fires `languageChanged` (sync html lang + persist)
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 px-2.5"
          aria-label={t('languageSwitcher.aria', { lang: currentLang.name })}
          data-testid="language-switcher-trigger"
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          <span aria-hidden="true" className={compact ? 'hidden min-[380px]:inline' : ''}>{currentLang.flag}</span>
          <span className="text-[11px] font-bold uppercase tracking-wider">{currentLang.code}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[180px]">
        {languages.map((lang) => {
          const active = lang.code === current;
          return (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`cursor-pointer flex items-center gap-2 ${active ? 'font-semibold bg-blue-50 text-blue-700' : ''}`}
              data-testid={`language-option-${lang.code}`}
            >
              <span aria-hidden="true">{lang.flag}</span>
              <span className="flex-1">{lang.name}</span>
              {active && <Check className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
